import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/HelpOutline';
import MapIcon from '@mui/icons-material/Map';
import SatelliteIcon from '@mui/icons-material/SatelliteOutlined';
import TextureIcon from '@mui/icons-material/Texture';
import {
  Avatar as AvatarUI,
  Box,
  Dialog,
  DialogContent,
  DialogTitle as DialogTitleUI,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Slide,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment, memo} from 'react';
import CopyButton from './CopyButton.jsx';

const Avatar = styled(AvatarUI)(({theme}) => ({
  backgroundColor: theme.palette.primary.main,
}));

const DialogTitle = styled(DialogTitleUI)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const RasterLayerDetailsDialog = ({
  layer,
  onClose,
  onBasemapViewerClick,
  bincat,
}) => (
  <Dialog
    disableEnforceFocus
    TransitionComponent={Slide}
    TransitionProps={{
      unmountOnExit: true,
      mountOnEnter: true,
      direction: 'up',
    }}
    fullWidth
    maxWidth="sm"
    open={layer !== null ? true : false}
    onClose={onClose}
    aria-labelledby="raster-layer-details-dialog-title"
    aria-describedby="raster-layer-details-dialog-description"
  >
    <DialogTitle id="raster-layer-details-dialog-title">
      <div>Layer Details</div>
      <IconButton aria-label="close" onClick={onClose} size="large">
        <CloseIcon color="error" />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      {layer && (
        <Fragment>
          <List>
            <ListItem>
              <ListItemAvatar>
                <Avatar>
                  <TextureIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText>
                <Typography variant="body1">Target Mosaic</Typography>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      paddingRight: '3px',
                      color: theme => theme.palette.grey.A400,
                    }}
                  >
                    Id:
                  </Typography>
                  <Typography variant="body2"> {layer.target.id} </Typography>
                  <CopyButton
                    title="Copy Target Mosaic Id"
                    value={layer.target.id}
                  />
                </Box>
              </ListItemText>
              <ListItemSecondaryAction>
                <Tooltip title="Open in Mosaics">
                  <IconButton
                    onClick={() => onBasemapViewerClick(layer.target.name)}
                    size="large"
                  >
                    <MapIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>

            <ListItem divider>
              <ListItemAvatar>
                <span></span>
              </ListItemAvatar>
              <ListItemText disableTypography>
                <Box display="flex" mb={3}>
                  <TextField
                    InputProps={{readOnly: true}}
                    fullWidth
                    label="XYZ Tiles Url"
                    variant="outlined"
                    value={layer.target._links.tiles + bincat}
                  />
                  <Box ml={1} display="flex" alignItems="center">
                    <CopyButton
                      size="medium"
                      title="Copy Target Mosaic XYZ Tiles Url"
                      value={layer.target._links.tiles + bincat}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    color="text.secondary"
                  >
                    <Tooltip title="Instructions on how to use XYZ tiles">
                      <IconButton
                        component="a"
                        href="https://developers.planet.com/docs/basemaps/tile-services/xyz/"
                        target="_blank"
                        color="inherit"
                        size="large"
                      >
                        <HelpIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListItemText>
            </ListItem>
            <br />
            <ListItem>
              <ListItemAvatar>
                <Avatar>
                  <SatelliteIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText>
                <Typography variant="body1">Source Mosaic</Typography>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      paddingRight: '3px',
                      color: theme => theme.palette.grey.A400,
                    }}
                  >
                    Name:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '70%',
                    }}
                  >
                    {layer.source.name}{' '}
                  </Typography>
                  <CopyButton
                    title="Copy Source Mosaic Name"
                    value={layer.source.name}
                  />
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      paddingRight: '3px',
                      color: theme => theme.palette.grey.A400,
                    }}
                  >
                    Id:
                  </Typography>
                  <Typography variant="body2">{layer.source.id} </Typography>
                  <CopyButton
                    title="Copy Source Mosaic Id"
                    value={layer.source.id}
                  />
                </Box>
              </ListItemText>
              <ListItemSecondaryAction>
                <Tooltip title="Open in Planet Basemaps Viewer">
                  <IconButton
                    onClick={() => onBasemapViewerClick(layer.source.name)}
                    size="large"
                  >
                    <MapIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>

            <ListItem>
              <ListItemAvatar>
                <span></span>
              </ListItemAvatar>
              <ListItemText disableTypography>
                <Box display="flex">
                  <TextField
                    InputProps={{readOnly: true}}
                    fullWidth
                    label="XYZ Tiles Url"
                    variant="outlined"
                    value={layer.source._links.tiles}
                  />
                  <Box ml={1} display="flex" alignItems="center">
                    <CopyButton
                      size="medium"
                      title="Copy Source Mosaic XYZ Tiles Url"
                      value={layer.source._links.tiles}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    color="text.secondary"
                  >
                    <Tooltip title="Instructions on how to use XYZ tiles">
                      <IconButton
                        component="a"
                        href="https://developers.planet.com/docs/basemaps/tile-services/xyz/"
                        target="_blank"
                        color="inherit"
                        size="large"
                      >
                        <HelpIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListItemText>
            </ListItem>
          </List>
        </Fragment>
      )}
    </DialogContent>
  </Dialog>
);

RasterLayerDetailsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  layer: PropTypes.object,
  onBasemapViewerClick: PropTypes.func.isRequired,
  bincat: PropTypes.string,
};

export default memo(RasterLayerDetailsDialog);
